import { useEffect, useRef, useCallback } from 'react';
import { signOut } from 'aws-amplify/auth';
import signOutWithCleanup from '../utils/signOutWithCleanup';

const DEFAULT_ACTIVITY_EVENTS = [
    'mousemove',
    'keydown',
    'scroll',
    'click',
    'touchstart',
];

/**
 * useIdleTimeout - monitors user activity and triggers warning/logout on inactivity.
 *
 * @param {Object} options
 * @param {number}   options.idleTimeout      - Idle time before warning appears in ms (default: 30 min)
 * @param {number}   options.warningDuration   - Extra time after warning before logout in ms (default: 2 min)
 * @param {function} options.onWarning         - Called when warning period begins
 * @param {function} options.onLogout          - Called after sign-out completes
 * @param {function} options.onActive          - Called if user becomes active during warning
 * @param {boolean}  options.globalSignOut     - Use global sign-out to revoke refresh token (default: true)
 * @param {string}   options.redirectUrl       - URL to redirect to after logout (default: '/login?reason=idle')
 * @param {string[]} options.activityEvents    - DOM events that count as activity
 * @param {boolean}  options.enabled           - Whether the timeout is active (default: true)
 *
 * @returns {{ stayLoggedIn: function }}
 */
export function useIdleTimeout({
    idleTimeout = 30 * 60 * 1000,
    warningDuration = 2 * 60 * 1000,
    onWarning,
    onLogout,
    onActive,
    globalSignOut = true,
    redirectUrl = '/login?reason=idle',
    activityEvents = DEFAULT_ACTIVITY_EVENTS,
    enabled = true,
} = {}) {
    const idleTimer = useRef(null);
    const warningTimer = useRef(null);
    const isWarning = useRef(false);

    // Store callbacks in refs so they don't cause timer resets on re-render
    const onWarningRef = useRef(onWarning);
    const onLogoutRef = useRef(onLogout);
    const onActiveRef = useRef(onActive);

    useEffect(() => { onWarningRef.current = onWarning; }, [onWarning]);
    useEffect(() => { onLogoutRef.current = onLogout; }, [onLogout]);
    useEffect(() => { onActiveRef.current = onActive; }, [onActive]);

    const handleLogout = useCallback(async () => {
        try {
            // Wrap signOut with cache cleanup — see signOutWithCleanup.js
            // header for the cross-user UI leak this closes.
            await signOutWithCleanup(signOut, { global: globalSignOut });
        } catch (err) {
            console.error('Idle timeout sign-out error:', err);
        }

        if (onLogoutRef.current) {
            onLogoutRef.current();
        } else if (redirectUrl) {
            window.location.href = redirectUrl;
        }
    }, [globalSignOut, redirectUrl]);

    const resetTimers = useCallback(() => {
        clearTimeout(idleTimer.current);
        clearTimeout(warningTimer.current);

        if (isWarning.current) {
            isWarning.current = false;
            onActiveRef.current?.();
        }

        // Warning fires after idleTimeout, logout fires after idleTimeout + warningDuration
        warningTimer.current = setTimeout(() => {
            isWarning.current = true;
            onWarningRef.current?.();
        }, idleTimeout);

        idleTimer.current = setTimeout(handleLogout, idleTimeout + warningDuration);
    }, [handleLogout, idleTimeout, warningDuration]);

    const stayLoggedIn = useCallback(() => {
        resetTimers();
    }, [resetTimers]);

    useEffect(() => {
        if (!enabled) return;

        activityEvents.forEach((event) =>
            document.addEventListener(event, resetTimers, { passive: true })
        );

        resetTimers();

        return () => {
            activityEvents.forEach((event) =>
                document.removeEventListener(event, resetTimers)
            );
            clearTimeout(idleTimer.current);
            clearTimeout(warningTimer.current);
        };
    }, [resetTimers, activityEvents, enabled]);

    return { stayLoggedIn };
}